import { Quote } from "lucide-react";
import { useContent, useLang } from "@/context/AppContext";
import { SectionHeader, Reveal } from "@/components/ui-bits";

// About — editorial spread: drop-capped lede column + pull-quote sidebar.
export default function Summary() {
  const { t, lang } = useLang();
  const { content } = useContent();
  const s = content?.settings || {};
  const summary = (s.summary?.[lang] || s.summary?.en || "").split("\n\n").filter(Boolean);
  const quote = s.quote?.[lang] || s.quote?.en || "";
  const points = t("summaryPoints");

  return (
    <section id="about" className="bg-obsidian py-20 lg:py-32" data-testid="summary-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeader eyebrow={t("summaryEyebrow")} title={t("summaryTitle")} index={1} />

        <div className="mt-12 grid gap-12 lg:grid-cols-12 lg:gap-10">
          {/* lede with magazine drop cap */}
          <div className="lg:col-span-7">
            {summary.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <p
                  className={`text-[17px] leading-[1.85] text-sand ${i === 0 ? "drop-cap" : "editorial-rule mt-6 pt-6 border-t-0"}`}
                >
                  {p}
                </p>
              </Reveal>
            ))}

            <ul className="mt-12 space-y-0 border-t border-hairline">
              {points.map((point, i) => (
                <Reveal key={i} delay={0.15 + i * 0.08}>
                  <li
                    className="group flex items-baseline gap-6 border-b border-hairline py-5 text-[15px] font-medium text-bone"
                    data-testid="summary-point"
                  >
                    <span className="font-mono text-[11px] font-bold tracking-[0.2em] text-vermilion">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="transition-transform duration-300 group-hover:translate-x-1">{point}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          {/* pull quote — sidebar */}
          <aside className="lg:col-span-5">
            <div className="grain-overlay relative border border-hairline bg-surface p-8 sm:p-10 lg:sticky lg:top-28" data-testid="summary-quote-card">
              <span className="font-display text-[80px] font-light leading-none text-vermilion" aria-hidden="true">
                &ldquo;
              </span>
              <Reveal delay={0.2}>
                <p
                  className="relative -mt-6 font-display text-2xl font-light italic leading-[1.4] text-bone sm:text-[27px]"
                  data-testid="summary-quote-text"
                >
                  {quote}
                </p>
              </Reveal>
              <div className="mt-8 flex items-center gap-4">
                <span className="h-px w-12 bg-vermilion" aria-hidden="true" />
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-sand">
                  {t("quoteLabel")}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
